import { observeWhenVisible } from '../../../core/utils/dom.js';
import { getCurrentLanguage } from '../../translations.js';
import { getAuthToken } from '../videoScheduleApi.js';
import { fetchStandaloneMediaObjectUrl, fetchStandaloneMediaPage, PAGE_LIMIT } from '../standaloneMediaApi.js';
import loginModalController from '../../components/LoginModalController.js';

const MODAL_ID = 'character-card-gallery-picker-modal';
const FILTERS = ['lowres', 'hires', 'none'];

const getT = (key, fallback) => {
  const lang = getCurrentLanguage() || 'en';
  const t = window.translations?.[lang]?.['create-character'] ?? window.translations?.en?.['create-character'];
  return t?.[key] ?? fallback;
};

const filterLabel = (value) => {
  switch (value) {
    case 'lowres':
      return getT('card_gallery_lowres', 'Low-res');
    case 'hires':
      return getT('card_gallery_hires', 'HD');
    case 'none':
      return getT('card_gallery_none', 'None');
    default: {
      const unreachable = value;
      throw new Error(`Unhandled gallery filter: ${String(unreachable)}`);
    }
  }
};

const isCardGalleryItemReady = (item) => {
  if (item?.media_kind === 'video') return item.status === 1 || item.status === 4;
  return item.status === 1 && !item.hires_pending;
};

const selectionKey = (item) => `${item?.media_kind || 'image'}:${item?.output_key || ''}`;

const ensureModal = () => {
  let modal = document.getElementById(MODAL_ID);
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${MODAL_ID}-title`);
  const panel = document.createElement('div');
  panel.className = 'picker-panel';
  modal.appendChild(panel);
  document.body.appendChild(modal);
  return modal;
};

const renderStatus = (grid, kind) => {
  grid.replaceChildren();
  const status = document.createElement('div');
  status.className = 'picker-status';
  const message = document.createElement('p');
  message.className = 'picker-status__message';
  if (kind === 'loading') {
    message.textContent = getT('card_gallery_loading', 'Loading…');
    status.appendChild(message);
  } else if (kind === 'login') {
    message.textContent = getT('card_gallery_login', 'Log in to choose from your gallery.');
    const loginButton = document.createElement('button');
    loginButton.type = 'button';
    loginButton.className = 'btn btn-secondary';
    loginButton.textContent = getT('card_gallery_login_button', 'Log in');
    loginButton.tabIndex = 0;
    loginButton.addEventListener('click', () => {
      loginModalController?.show?.();
    });
    status.appendChild(message);
    status.appendChild(loginButton);
  } else if (kind === 'error') {
    message.textContent = getT('card_gallery_error', 'Could not load your gallery.');
    status.appendChild(message);
  } else {
    message.textContent = getT('card_gallery_empty', 'Nothing in this filter yet.');
    status.appendChild(message);
  }
  grid.appendChild(status);
};

const appendFilterGroup = (parent, label, groupLabel) => {
  const row = document.createElement('div');
  row.className = 'card-gallery-filter-row';
  const name = document.createElement('span');
  name.className = 'card-gallery-filter-label';
  name.textContent = label;
  const group = document.createElement('div');
  group.className = 'create-mode-tabs create-mode-tabs--nested';
  group.setAttribute('role', 'radiogroup');
  group.setAttribute('aria-label', groupLabel);
  FILTERS.forEach((value) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'create-mode-tab';
    button.dataset.hd = value;
    button.setAttribute('role', 'radio');
    button.tabIndex = 0;
    button.textContent = filterLabel(value);
    group.appendChild(button);
  });
  row.append(name, group);
  parent.appendChild(row);
  return group;
};

export const openCharacterCardGalleryPicker = async ({ onSelect, selectedKey = '', onClose } = {}) => {
  const modal = ensureModal();
  const panel = modal.querySelector('.picker-panel');
  if (!panel) return;

  let imagesHd = 'hires';
  let videosHd = 'hires';
  let nextCursor = null;
  let isLoadingMore = false;
  let loadGeneration = 0;
  let closed = false;
  let sentinelObserver = { disconnect: () => {} };

  const handleClose = () => {
    if (closed) return;
    closed = true;
    sentinelObserver.disconnect();
    if (cleanup.keydown) document.removeEventListener('keydown', cleanup.keydown, true);
    cleanup.keydown = null;
    modal.classList.remove('show');
    modal.onclick = null;
    onClose?.();
  };

  const cleanup = { keydown: null };

  const appendTiles = (grid, items, token) => {
    items.forEach((item) => {
      const ready = isCardGalleryItemReady(item);
      const isVideo = item.media_kind === 'video';
      const card = document.createElement('div');
      card.className = 'image-picker-card';
      card.setAttribute('role', 'button');
      card.tabIndex = 0;
      card.setAttribute('aria-disabled', ready ? 'false' : 'true');
      card.setAttribute(
        'aria-label',
        ready
          ? getT(isVideo ? 'card_gallery_use_video' : 'card_gallery_use_image', isVideo ? 'Use this video' : 'Use this image')
          : getT('card_gallery_pending', 'Not ready yet'),
      );
      if (!ready) card.classList.add('image-picker-card--pending');
      if (selectedKey && selectionKey(item) === String(selectedKey)) card.classList.add('is-selected');

      if (ready) {
        if (isVideo) {
          const video = document.createElement('video');
          video.muted = true;
          video.playsInline = true;
          video.preload = 'metadata';
          card.appendChild(video);
          void (async () => {
            const objectUrl = await fetchStandaloneMediaObjectUrl({ token, item });
            if (objectUrl && video.isConnected) video.src = objectUrl;
          })();
        } else {
          const img = document.createElement('img');
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          card.appendChild(img);
          void (async () => {
            const objectUrl = await fetchStandaloneMediaObjectUrl({ token, item });
            if (objectUrl && img.isConnected) img.src = objectUrl;
          })();
        }

        const handleActivate = (event) => {
          event.preventDefault();
          event.stopPropagation();
          handleClose();
          onSelect?.(item);
        };
        card.addEventListener('click', handleActivate);
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          handleActivate(event);
        });
      }

      grid.appendChild(card);
    });
  };

  const loadPage = async (grid, sentinel, { append = false } = {}) => {
    if (imagesHd === 'none' && videosHd === 'none') {
      if (!append) {
        loadGeneration += 1;
        renderStatus(grid, 'empty');
        nextCursor = null;
        sentinel.hidden = true;
      }
      return;
    }
    if (append) {
      if (!nextCursor || isLoadingMore) return;
    }
    const generation = append ? loadGeneration : ++loadGeneration;
    if (append) isLoadingMore = true;
    if (!append) {
      nextCursor = null;
      renderStatus(grid, 'loading');
    }
    try {
      const token = await getAuthToken();
      if (!token) {
        if (generation !== loadGeneration) return;
        renderStatus(grid, 'login');
        return;
      }
      const result = await fetchStandaloneMediaPage({
        token,
        cursor: append ? nextCursor : null,
        imagesHd,
        videosHd,
        limit: PAGE_LIMIT,
      });
      if (generation !== loadGeneration) return;
      const items = Array.isArray(result?.items) ? result.items : [];
      if (!append) {
        grid.replaceChildren();
        if (items.length === 0) {
          renderStatus(grid, 'empty');
          nextCursor = null;
          sentinel.hidden = true;
          return;
        }
      }
      if (items.length > 0) appendTiles(grid, items, token);
      nextCursor = result?.nextCursor || null;
      sentinel.hidden = !nextCursor;
    } catch (error) {
      if (generation !== loadGeneration) return;
      if (!append) renderStatus(grid, error?.code === 'login' ? 'login' : 'error');
      console.error('[characterCardGalleryPicker] load failed', error);
    } finally {
      if (generation === loadGeneration) {
        if (append) isLoadingMore = false;
      }
    }
  };

  panel.replaceChildren();
  const header = document.createElement('div');
  header.className = 'picker-header';
  const title = document.createElement('div');
  title.className = 'picker-title';
  title.id = `${MODAL_ID}-title`;
  title.textContent = getT('card_gallery_title', 'Choose from your gallery');
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'picker-close';
  closeBtn.textContent = '×';
  closeBtn.setAttribute('aria-label', getT('card_cancel_upload', 'Cancel'));
  closeBtn.tabIndex = 0;
  closeBtn.addEventListener('click', handleClose);
  header.append(title, closeBtn);

  const filters = document.createElement('div');
  filters.className = 'card-gallery-filters';
  const imageGroup = appendFilterGroup(
    filters,
    getT('card_gallery_images', 'Images'),
    getT('card_gallery_images', 'Images'),
  );
  const videoGroup = appendFilterGroup(
    filters,
    getT('card_gallery_videos', 'Videos'),
    getT('card_gallery_videos', 'Videos'),
  );

  const listWrap = document.createElement('div');
  listWrap.className = 'picker-list';
  const grid = document.createElement('div');
  grid.className = 'picker-grid image-picker-grid';
  const sentinel = document.createElement('div');
  sentinel.className = 'infinite-scroll-trigger';
  sentinel.hidden = true;
  listWrap.append(grid, sentinel);
  panel.append(header, filters, listWrap);

  const bindGroup = (group, getValue, setValue) => {
    const buttons = [...group.querySelectorAll('[data-hd]')];
    const sync = () => {
      const current = getValue();
      buttons.forEach((button) => {
        const selected = button.dataset.hd === current;
        button.classList.toggle('create-mode-tab--active', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
    };
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const next = button.dataset.hd;
        if (next !== 'lowres' && next !== 'hires' && next !== 'none') return;
        if (next === getValue()) return;
        setValue(next);
        sync();
        void loadPage(grid, sentinel, { append: false });
      });
    });
    sync();
  };

  bindGroup(imageGroup, () => imagesHd, (value) => { imagesHd = value; });
  bindGroup(videoGroup, () => videosHd, (value) => { videosHd = value; });

  sentinelObserver = observeWhenVisible(sentinel, () => {
    if (nextCursor) void loadPage(grid, sentinel, { append: true });
  }, { root: listWrap, rootMargin: '100px' });

  cleanup.keydown = (event) => {
    const key = event.key || event.code;
    if (key !== 'Escape' && key !== 'Esc') return;
    event.preventDefault();
    event.stopPropagation();
    handleClose();
  };
  document.addEventListener('keydown', cleanup.keydown, true);
  modal.onclick = (event) => {
    if (event.target === modal) handleClose();
  };

  modal.classList.add('show');
  await loadPage(grid, sentinel, { append: false });
};
